import { ActionIcon, Button, Group, Loader, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy, IconMail, IconVolume } from '@tabler/icons';
import mixpanel from 'mixpanel-browser';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getTTS, RobotInfo } from '@mygpt/app/apis';
import { useBotMethods } from '@mygpt/app/bot';
import { useChatStore } from '@mygpt/app/chat';
import { Markdown } from '@mygpt/app/chat/components';
import { CanvasToggleButton } from '@mygpt/app/chat/components/Canvas/ToggleButton';
import FaqChildNodeList from '@mygpt/app/chat/components/FaqChildNodeList';
import { MESSAGE_SIGNIN_ICON_ARRAY, SvgAlert, SvgRefresh } from '@mygpt/app/chat/components/icon';
import LikeButton from '@mygpt/app/chat/components/LikeButton';
import PdfViewer from '@mygpt/app/chat/components/PdfViewer';
import RecommendList from '@mygpt/app/chat/components/RecommendList';
import { TransferToHuman } from '@mygpt/app/chat/components/TransferToHuman';
import { signatureClickAction } from '@mygpt/app/chat/services';
import { checkIsUrl, copyToClipboard, cleanMarkdownText, splitIntoSentences } from '@mygpt/app/utils';
import { ratingType, ReferenceSign, SessionListParam } from '@mygpt/types';
import { SvgPlayStop } from './icon';
import { FetchQuestionOptions, useAudioManager } from '@mygpt/app/chat/hooks';

import classes from './chatContent.module.css';
import { PhoneList } from '@mygpt/app/chat/components/CustomizedComponents/PhoneList';

type SignatureMessagesDisplayType = 'all' | 'widget' | 'share' | 'desktop';

export interface I_SignatureMessages {
  checked: boolean;
  value: string;
  displayType: SignatureMessagesDisplayType;
  list: {
    iconIndex: number;
    value: string;
  }[];
}

interface ChatContentProps {
  id: string;
  item: SessionListParam;
  index: number;
  answerSource: boolean;
  pending?: string;
  chatMessages: SessionListParam[];
  sessionId: string;
  // 是否把工具条按钮显示在消息的右上方
  floatingActions?: boolean;
  signatureMessages?: I_SignatureMessages;
  pageSource: 'widget' | 'playground' | 'share';
  robotInfo?: RobotInfo;
  isPlaying?: boolean;
  ttsPause?: boolean;
  ttsLanguage?: string;
  onAudioControl?: (messageId: string | null, fullAnswerPlay?: boolean) => void;

  fetchQuestion: (value: string, options?: FetchQuestionOptions) => void;
  isLastMessage: (id: string) => boolean;
  regenerate: (value: string) => void;
  handleMessages: (obj: { rating: number; messageId: string }) => void;
  getReference: (ai_id: string, message_id: string) => Promise<ReferenceSign[]>;
  isDigitalHuman?: boolean;
}

export const ChatContent: React.FC<ChatContentProps> = (props) => {
  const { t } = useTranslation();
  const { setRobotInfo } = useBotMethods();
  const { setPageSource, ttsMuted } = useChatStore();

  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(!copied);
      }, 4 * 1000);
    }
  }, [copied]);

  const [referenceFileInfo, setReferenceFileInfo] = useState<{
    fileId: string;
    pageNumbers: number[];
  }>({
    fileId: '',
    pageNumbers: [],
  });

  const {
    answerSource,
    chatMessages,
    fetchQuestion,
    getReference,
    handleMessages,
    id,
    index,
    isLastMessage,
    isPlaying,
    item,
    onAudioControl,
    pageSource,
    pending,
    regenerate,
    robotInfo,
    sessionId,
    signatureMessages,
    floatingActions,
    ttsPause,
    ttsLanguage,
    isDigitalHuman,
  } = props;

  const isWidget = pageSource === 'widget';

  const enableTTS = robotInfo?.configs.enable_tts || (isWidget && robotInfo?.configs.enable_2d_digital_person);

  useEffect(() => {
    setPageSource(pageSource || undefined);
  }, [pageSource]);

  useEffect(() => {
    robotInfo && setRobotInfo(robotInfo);
  }, [robotInfo]);

  const referencePage = useMemo(() => {
    const { reference_indexes, reference_list } = item;
    if (!reference_list?.length || !reference_indexes?.length) return [];
    const indexes = reference_indexes.map((index) => index.id);
    return reference_list?.filter((reference) => {
      return indexes.includes(reference.id);
    });
  }, [item.reference_list, item.reference_indexes]);

  const handleSignatureClick = ({ content, icon }: { icon: string; content: string }) => {
    signatureClickAction({ ai_id: id, session_id: sessionId, icon, content });
    const iconType = ['Email', 'Contact Number', 'Location', 'Fax Number', 'Link', 'Date', 'Time', 'Text'];
    isWidget &&
      mixpanel.track('signature_message_clicked', {
        iconIndex: icon,
        iconType: iconType[+icon],
        value: content,
      });
  };

  const [ttsLoading, setTtsLoading] = useState<boolean>(false);
  // 分句播放音频管理
  const audioManager = useAudioManager({
    isDigitalHuman: isDigitalHuman,
  });
  // 整句播放音频管理
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioCache = useRef<string>(''); // 音频缓存

  const hasPausedRef = useRef<boolean>(false); // 暂停标记
  const isGettingTTSRef = useRef<boolean>(false); // tts队列请求状态标记

  useEffect(() => {
    // 如果正在获取tts/正在播放，监听到父元素有操作打断，则标记暂停
    if (ttsPause && (isGettingTTSRef.current || audioManager.isPlaying)) {
      hasPausedRef.current = true;
    }
  }, [ttsPause, audioManager.isPlaying]);

  useEffect(() => {
    // 监听到暂停标记为true时调用打断方法，停止tts请求、停止分段播放、停止整段播放
    hasPausedRef.current && handlePause();
  }, [hasPausedRef.current]);

  const handlePause = () => {
    // 停止整段播放
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    // 停止分段播放
    if (audioManager.isPlaying) {
      audioManager.setAudioQueue([]);
      audioManager.stopAudio();
    }
  };

  // 点击消息气泡下方的播放/停止icon，整段播放/停止
  const handleTTSPlayOrPause = async (text: string) => {
    hasPausedRef.current = false;
    if (isPlaying) {
      handlePause();
      onAudioControl?.(null);
      return;
    }

    setTtsLoading(true);
    // 通知父组件开始播放当前消息
    onAudioControl?.(item.messageId || null, true);

    // 完整的text文本清理
    const cleanText = cleanMarkdownText(text);

    try {
      // 检查是否有缓存
      if (audioCache.current) {
        audioRef.current!.src = audioCache.current;
      } else {
        const audioBlob = await getTTS(cleanText);
        const audioUrl = window.URL.createObjectURL(audioBlob);
        // 缓存音频
        audioCache.current = audioUrl;
        audioRef.current!.src = audioUrl;
      }
      setTimeout(() => {
        audioRef.current?.play();
        setTtsLoading(false);
      }, 500);
    } catch (error) {
      console.error('Failed to getTTS:', error);
      onAudioControl?.(null);
      setTtsLoading(false);
    }
  };

  useEffect(() => {
    if (!isPlaying && !audioRef.current?.paused) {
      handlePause();
    }
  }, [isPlaying]);

  const sentenceQueueRef = useRef<string[]>([]); // 分句队列
  const splitIndexRef = useRef<number>(0); // 分句索引
  const hasHadPendingRef = useRef<boolean>(false); // 跟踪该条消息是否曾经有过 pending 内容

  // 流式输出过程中，处理markdown文本，分句
  useEffect(() => {
    const shouldProcess =
      item.text &&
      chatMessages.at(-1)?.messageId === item.messageId &&
      !ttsMuted &&
      item.isAnswer &&
      enableTTS &&
      !hasPausedRef.current &&
      (pending || hasHadPendingRef.current); // 只有正在流式输出或曾经有过 pending 的消息才处理

    if (shouldProcess && pending) {
      // 标记该条消息曾经有过 pending 内容
      hasHadPendingRef.current = true;

      const [sentences, newIndex] = splitIntoSentences(cleanMarkdownText(item.text), splitIndexRef.current);
      splitIndexRef.current = newIndex;

      if (sentences.length > 0) {
        sentenceQueueRef.current = [...sentenceQueueRef.current, ...sentences.map((text) => text)];
      }
    }

    if (shouldProcess && !pending && hasHadPendingRef.current) {
      const remainingText = cleanMarkdownText(item.text).slice(splitIndexRef.current).trim();
      // 流式输出结束后，还有剩余内容，整体推进队列
      if (remainingText) {
        sentenceQueueRef.current = [...sentenceQueueRef.current, remainingText];
      }
    }
  }, [item.text, pending]);

  // 获取音频队列索引
  const getTTSIndexRef = useRef<number>(0);

  // 获取音频队列
  const getTTSQueue = async () => {
    console.log('getTTSQueue状态:', {
      sentenceQueueRef: sentenceQueueRef.current,
      getTTSIndexRef: getTTSIndexRef.current,
      isGettingTTSRef: isGettingTTSRef.current,
    });

    if (ttsMuted || hasPausedRef.current) return;
    // 如果请求的句子已经全部处理完毕，则恢复请求tts的状态标记
    if (getTTSIndexRef.current >= sentenceQueueRef.current.length) {
      isGettingTTSRef.current = false;
      return;
    }

    isGettingTTSRef.current = true;
    const sentence = sentenceQueueRef.current[getTTSIndexRef.current];
    console.log('请求tts', sentence);

    const isDnpAvatar =
      id &&
      [
        '5d82886e-a515-489e-a6ad-ad5099864a64',
        '28b48307-4701-4cf1-872a-cdadbafa6361',
        '6e96a3a8-3a29-48dd-8ef0-d2bc6744863c',
      ].includes(id);

    const audioBlob = await getTTS(
      sentence,
      ttsLanguage,
      isDnpAvatar && ttsLanguage === 'ja' ? 'ja-JP-DaichiNeural' : undefined
    );
    const audioUrl = URL.createObjectURL(audioBlob);

    audioManager.setAudioQueue((prev) => [...prev, audioUrl]);
    if (getTTSIndexRef.current < sentenceQueueRef.current.length) {
      getTTSIndexRef.current += 1;
    }
    getTTSQueue();
  };

  // 分句队列发生变化，且tts请求队列没有在进行时，调用请求tts队列的方法，其它情况下getTTSQueue会自行递归调用
  // 仅发生在产生了第一个分句/tts请求结束了才产生了新的分句时
  useEffect(() => {
    console.log('useEffect sentenceQueue', {
      sentenceQueue: sentenceQueueRef.current,
      getTTSIndexRef: getTTSIndexRef.current,
      hasPausedRef: hasPausedRef.current,
    });
    if (sentenceQueueRef.current.length > 0 && !isGettingTTSRef.current && !hasPausedRef.current) {
      getTTSQueue();
    }
  }, [sentenceQueueRef.current.length]);

  // 流式输出过程中，通知父组件当前播放的音频id
  useEffect(() => {
    if (!item.messageId || !enableTTS) return;
    if (audioManager.isPlaying) {
      onAudioControl?.(audioManager.isPlaying ? item.messageId : null);
    } else if (!pending && !isGettingTTSRef.current) {
      onAudioControl?.(null);
    }
  }, [audioManager.isPlaying, pending]);

  if (!item.isAnswer) {
    return (
      <pre className={`text-[14px] text-gray-700 whitespace-break-spaces ${isDigitalHuman && 'text-white'}`}>
        {item.text}
      </pre>
    );
  }

  return (
    <>
      <audio
        ref={audioRef}
        style={{ display: 'none' }}
        onEnded={() => {
          onAudioControl?.(null);
        }}
      />
      <Markdown
        className={`text-[14px]`}
        content={item.text}
        footnotes={answerSource ? item.footnotes : []}
        referenceList={answerSource ? item.reference_list : []}
        referenceIndexes={answerSource ? item.reference_indexes : []}
        getReference={item.messageId && answerSource ? () => getReference(id, item.messageId!) : undefined}
        clickReference={(fileId, pageNumbers) => {
          setReferenceFileInfo({ fileId, pageNumbers });
        }}
        isDigitalHuman={isDigitalHuman}
      />
      {item.prompt && <pre className="text-[14px] text-gray-600 whitespace-break-spaces mt-5">{item.prompt}</pre>}
      {item.source && !!item.source.length && !isDigitalHuman && (
        <>
          <Text c="gray.5" size={'xs'} fw={300} mt={12}>
            {t('completions.source')}:
          </Text>
          <Group mt={8} gap={12}>
            {item.source.map((item, index) => (
              <Button
                key={index}
                className={classes['btnTag']}
                size="sm"
                component="a"
                href={item}
                target={'_blank'}
                disabled={!checkIsUrl(item, true)}
              >
                {item}
              </Button>
            ))}
          </Group>
        </>
      )}
      {item.faq_child_node && !!item.faq_child_node.length && !isDigitalHuman && (
        <section className="my-5">
          <FaqChildNodeList aiId={id} list={item.faq_child_node} onClick={fetchQuestion} />
        </section>
      )}
      {item.recommend_list && !!item.recommend_list.length && !isDigitalHuman && (
        <section className="pt-5 pb-2 mt-5 mb-2 rounded-xl border-t border-gray-200">
          <Text c="gray.4" size={'sm'} mt={5} mb={6}>
            {t('completions.related')}
          </Text>
          <RecommendList aiId={id} list={item.recommend_list} onClick={fetchQuestion} />
        </section>
      )}
      {item.clarification && item.clarification === 'phone' && <PhoneList onClick={fetchQuestion} />}
      {item.error && (
        <div className="text-[14px] leading-7">
          <div className="flex items-center gap-2 text-red-500">
            <SvgAlert />
            {t('completions.error')}
          </div>
          <div className="flex gap-2 flex-col sm:flex-row items-start sm:items-center">
            <Text c={'red.4'}>{item.error}</Text>
            {isLastMessage(item.messageId || '') && item.error && (
              <Button
                variant="subtle"
                color="violet.5"
                size="compact-xs"
                leftSection={<SvgRefresh />}
                onClick={() => {
                  regenerate(chatMessages[index - 1].text);
                }}
              >
                {t('completions.regenerate')}
              </Button>
            )}
          </div>
        </div>
      )}
      {item.isAnswer && !item.error && !item.userAborted && !isDigitalHuman && (
        <>
          {(!pending || index !== chatMessages.length - 1) && signatureMessages && (
            <section className="my-2">
              <Text size={'xs'} c="gray.4">
                {signatureMessages.value}
              </Text>
              {!!signatureMessages.list.length && (
                <ul className="bg-white rounded-lg px-2 py-3 mt-2 space-y-3">
                  {signatureMessages.list.map((item, index) => {
                    const ItemIcon = MESSAGE_SIGNIN_ICON_ARRAY[item.iconIndex];
                    return (
                      <li key={index}>
                        <Group wrap="nowrap" gap={5}>
                          <section className="p-1">
                            <ItemIcon />
                          </section>
                          <Text
                            c="gray.4"
                            size="sm"
                            lh={'1.2rem'}
                            className="cursor-pointer"
                            onClick={() => {
                              handleSignatureClick({ icon: item.iconIndex.toString(), content: item.value });
                            }}
                          >
                            {item.iconIndex === 0 && <a href={`mailto:${item.value}`}>{item.value}</a>}
                            {item.iconIndex === 1 && <a href={`tel:${item.value}`}>{item.value}</a>}
                            {item.iconIndex === 2 && (
                              <a href={`https://maps.google.com/maps?q=${item.value}`} target="_blank" rel="noreferrer">
                                {item.value}
                              </a>
                            )}
                            {item.iconIndex === 4 && (
                              <a href={`${item.value}`} target="_blank" rel="noreferrer">
                                {item.value}
                              </a>
                            )}
                            {![0, 1, 2, 4].includes(item.iconIndex) && item.value}
                          </Text>
                        </Group>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          {!(pending && chatMessages.at(-1)?.messageId === item.messageId) && item.messageId && (
            <div
              className={`${
                floatingActions && 'xl:absolute xl:right-0 xl:top-0 xl:translate-x-full xl:pl-2 pt-1'
              } mt-1`}
            >
              <Group justify="space-between">
                <Group gap={5} wrap="nowrap">
                  {enableTTS && (
                    <Tooltip
                      label={isPlaying ? t('completions.tts.stop') : t('completions.tts.play')}
                      position="bottom"
                      withArrow
                    >
                      <ActionIcon variant={'subtle'} color="indigo" onClick={() => handleTTSPlayOrPause(item.text)}>
                        {ttsLoading ? <Loader size="xs" /> : isPlaying ? <SvgPlayStop /> : <IconVolume color="gray" />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                  <LikeButton
                    rating={item.rating as ratingType}
                    aiId={id}
                    sessionId={sessionId}
                    messageId={item.messageId || ''}
                    callback={handleMessages}
                    pageSource={pageSource}
                  />

                  <ActionIcon
                    variant={'subtle'}
                    color="indigo"
                    onClick={() => {
                      const copied = copyToClipboard(item.text);
                      setCopied(!!copied);
                    }}
                  >
                    {copied ? <IconCheck color="teal" /> : <IconCopy color="gray" />}
                  </ActionIcon>
                  {item.llm_can_answer === false && (
                    <TransferToHuman messageId={item.messageId || ''} sessionId={sessionId} pageSource={pageSource}>
                      <ActionIcon variant={'subtle'} color="indigo">
                        <Tooltip label={t('settings.transfer.title')} withArrow>
                          <span>
                            <IconMail color="gray" />
                          </span>
                        </Tooltip>
                      </ActionIcon>
                    </TransferToHuman>
                  )}
                </Group>
                <CanvasToggleButton
                  messageId={item.messageId}
                  canvas_url={item.canvas_url}
                  canvas_title={item.canvas_title}
                />
              </Group>
            </div>
          )}
        </>
      )}
      {!item.text && item.userAborted && (
        <Text size="sm" c="gray.4">
          {t('canceled')}
        </Text>
      )}
      <PdfViewer
        fileId={referenceFileInfo.fileId}
        pageNumbers={referenceFileInfo.pageNumbers}
        referencePage={referencePage}
        onClose={() => {
          setReferenceFileInfo({ fileId: '', pageNumbers: [] });
        }}
      />
    </>
  );
};
